<template>
    <div class="id-scope home-page">
        <!-- ── Greeting header ── -->
        <div class="pg-head">
            <div class="pg-ico"><span class="mdi mdi-home-account"></span></div>
            <div>
                <div class="id-h1">{{ $t('home.title') }} 🌟</div>
                <div class="pg-sub">{{ $t('home.familySection') }}</div>
            </div>
        </div>

        <!-- ⚠️ แถบเตือน "คอร์สหมดอายุ / เหลือน้อย / ใกล้หมดอายุ" เคยอยู่ตรงนี้ — ย้ายไปเป็น
             การ์ดในกระดิ่งแจ้งเตือนแล้ว (เจ้าของสั่ง 2026-08-05) เพราะแถบนี้เห็นได้ต่อเมื่อ
             ผู้ปกครองเปิดหน้าแรกอยู่พอดี ส่วนการ์ดตามไปถึงเครื่องผ่าน Web Push ได้ด้วย
             ตรรกะทั้งหมดอยู่ฝั่ง backend แล้ว (istarserver_dev/courseAlerts.js + cron 09:00)
             **อย่าเพิ่มแถบกลับมาคู่กัน** — จะกลายเป็นเตือนซ้ำสองที่ด้วยเกณฑ์คนละชุด -->

        <!-- ── Empty state ── -->
        <div v-if="familylist == null || familylist.length === 0" class="scard" style="padding:0">
            <div class="id-empty">
                <div class="mdi mdi-account-off-outline"></div>
                <div style="margin-top:8px">{{ $t('home.addFamilyHint') }}</div>
            </div>
        </div>

        <template v-else>
            <!-- ── Group booking entry — เฉพาะครอบครัวที่มีลูก(มีคอร์ส) ตั้งแต่ 2 คนขึ้นไป ── -->
            <div v-if="groupBookableCount >= 2" class="scard home-group-entry" style="padding:16px 20px;margin-bottom:16px">
                <div class="row" style="gap:10px;flex-wrap:wrap">
                    <span class="mdi mdi-account-multiple-plus" style="font-size:26px;color:var(--c-primary)"></span>
                    <div style="flex:1;min-width:180px">
                        <div class="strong" style="color:var(--c-text-heading)">
                            {{ $t('groupbooking.title') }}
                            <span class="badge badge-success" style="margin-left:6px">{{ $t('groupbooking.entryNew') }}</span>
                        </div>
                        <div class="t-cap">{{ $t('groupbooking.subtitle') }}</div>
                    </div>
                    <button type="button" class="id-btn id-btn-primary" @click="doGroupReservation">
                        <span class="mdi mdi-calendar-multiple-check"></span> {{ $t('groupbooking.entryBtn') }}
                    </button>
                </div>
            </div>

            <!-- ── Family students ── -->
            <div class="isk-sec" style="margin-bottom:10px">{{ $t('home.familySection') }}</div>
            <div class="cards3" style="margin-bottom:24px">
                <div v-for="p in familylist" :key="p.studentid"
                     class="scard home-scard"
                     :class="{ 'home-scard--active': p.studentid === studentid }"
                     style="padding:20px"
                     @click="selectChild(p)">
                    <div class="row" style="gap:12px;margin-bottom:12px">
                        <span class="tt-avatar home-card-avatar" style="background:var(--c-primary)">
                            <img :src="p.profile_image_url || (p.gender === 'หญิง' ? profileGirl : profileBoy)"
                                 alt="" />
                        </span>
                        <div style="flex:1;min-width:0">
                            <div class="strong" style="font-size:17px;color:var(--c-text-heading)">{{ p.fullname }}</div>
                            <div class="t-cap">
                                <template v-if="p.course_shortname">{{ p.course_shortname }} · </template>{{ $t('home.genderAge', { gender: p.gender, age: calculateAge(p.dateofbirth) }) }}
                            </div>
                        </div>
                        <span class="badge"
                              :class="!p.courserefer ? 'badge-neutral' : (p.expiredate && isExpire(p.expiredate)) ? 'badge-error' : p.coursetype === 'Monthly' ? 'badge-success' : (p.remaining <= 0) ? 'badge-error' : (p.remaining <= 2) ? 'badge-warning' : 'badge-success'">
                            <template v-if="!p.courserefer">{{ $t('home.noCourse') }}</template>
                            <template v-else-if="p.expiredate && isExpire(p.expiredate)">{{ $t('home.expired') }}</template>
                            <template v-else-if="p.coursetype === 'Monthly'">{{ $t('home.monthly') }}</template>
                            <template v-else-if="p.remaining <= 0">{{ $t('home.usedUp') }}</template>
                            <template v-else>{{ $t('home.remainBadge', { n: p.remaining }) }}</template>
                        </span>
                    </div>

                    <!-- per-session: remaining count + segmented "pips" (one block per session,
                         filled = remaining; empties as classes are used = "ค่อยๆ พร่องลง") -->
                    <div v-if="p.courserefer && p.coursetype !== 'Monthly'" style="margin-bottom:10px">
                        <div class="row" style="justify-content:space-between;font-size:12.5px;margin-bottom:5px">
                            <span class="muted">{{ $t('table.remaining') }}</span>
                            <span class="strong">{{ remainingText(p) }}</span>
                        </div>
                        <!-- used (faded) pips sit at the FRONT, remaining (filled) at the BACK;
                             as sessions are used the filled part recedes toward the back -->
                        <div class="quota-pips" :aria-label="remainingText(p)">
                            <span v-for="i in pipCount(p)" :key="i" class="quota-pip"
                                  :class="{ on: i > pipCount(p) - remainNum(p) }"
                                  :style="i > pipCount(p) - remainNum(p) ? { background: pipFill(p) } : null"></span>
                        </div>
                    </div>
                    <!-- monthly: time-to-expiry bar (falls back to the current month when no expiry is stored) -->
                    <div v-else-if="p.courserefer && p.coursetype === 'Monthly'" style="margin-bottom:10px">
                        <div class="row" style="justify-content:space-between;font-size:12.5px;margin-bottom:5px">
                            <span class="muted">{{ $t('home.timeLeftLabel') }}</span>
                            <span class="strong">{{ monthlyDaysText(p) }}</span>
                        </div>
                        <!-- remaining time sits on the RIGHT and recedes right as days pass
                             (same "draining" direction as the per-session pips) -->
                        <div class="quota-bar">
                            <div class="quota-bar-fill" :style="{ width: monthlyPct(p) + '%', background: monthlyFill(p) }"></div>
                        </div>
                    </div>
                    <!-- แพ็กเกจที่ใช้ร่วมกับเด็กคนอื่น — บอกแค่ว่าใช้ร่วมกับใคร (เจ้าของเคาะ 6 ก.ย. 69)
                         ไม่ต้องอธิบายเรื่องโควตาต่อท้าย เพราะคำว่า "ใช้ร่วมกับ" สื่ออยู่แล้ว
                         แพ็กเกจส่วนใหญ่มีคนถือคนเดียว บรรทัดนี้จึงไม่โผล่ในการ์ดปกติเลย -->
                    <div v-if="p.sharedNames && p.sharedNames.length" class="shared-line">
                        <span class="mdi mdi-account-multiple-outline"></span>
                        <span>{{ $t('home.sharedWith', { names: p.sharedNames.join(', ') }) }}</span>
                    </div>
                    <div class="row" style="justify-content:space-between;font-size:13px;margin-bottom:14px">
                        <span>{{ p.coursename || p.course_shortname || $t('home.noCourse') }}</span>
                        <span v-if="p.expiredate" class="muted">{{ $t('table.expireDate') }} {{ format_date(p.expiredate) }}</span>
                    </div>

                    <button class="id-btn id-btn-primary" style="width:100%"
                            @click.stop="selectChild(p); doReservation()">
                        <span class="mdi mdi-calendar-plus"></span> {{ $t('home.bookClass') }}
                    </button>
                </div>
            </div>

            <!-- ── อีเวนต์/แข่งขัน: เปิดรับสมัคร + การสมัครของครอบครัว (เฟส 4) ──
                 ซ่อนตัวเองเมื่อไม่มีอะไรให้แสดง จึงวางไว้ตรงนี้ได้โดยไม่ดันบล็อกอื่นทิ้งช่องว่างเปล่า -->
            <event-signup @onLoading="v => $emit('onLoading', v)"
                @onErrorHandler="m => $emit('onErrorHandler', m)"
                @onSuccessHandler="m => $emit('onSuccessHandler', m)"></event-signup>

            <!-- ── Reservation schedule of selected member ── -->
            <div class="isk-sec" style="margin:24px 0 10px">{{ $t('home.reservation') }}<span v-if="studentSelected && studentSelected.nickname" class="home-sec-sub"> · {{ studentSelected.nickname }}</span></div>
            <div class="scard" style="padding:0">
                <div v-if="!studentSelected" class="id-empty">
                    <div class="mdi mdi-gesture-tap"></div>
                    <div style="margin-top:8px">{{ $t('home.selectMember') }}</div>
                </div>
                <!-- loading: skeleton rows so switching members feels like a fresh fetch, not a flash -->
                <div v-else-if="loadingHistory">
                    <div v-for="i in 3" :key="'hsk' + i" class="row home-hist-row">
                        <div class="id-skel" style="width:38px;height:38px;border-radius:10px"></div>
                        <div class="id-skel" style="height:13px;width:55%"></div>
                    </div>
                </div>
                <div v-else-if="memberReservationDetail && memberReservationDetail.length > 0" class="id-fade-in" :key="studentid">
                    <!-- หมายเลขคอร์สที่ใช้จองคาบนั้น + ปุ่มยกเลิก แสดงในแถวเลย ไม่ต้องกด (ผู้ปกครองใช้อ้างอิงเวลาติดต่อแอดมิน) -->
                    <div v-for="item in memberReservationDetail" :key="item.classdate + item.classtime" class="home-hist-item">
                        <div class="row home-hist-row">
                            <div class="home-datebadge">
                                <span class="home-datebadge-d">{{ dateBadge(item.classdate).d }}</span>
                                <span class="home-datebadge-m">{{ dateBadge(item.classdate).m }}</span>
                            </div>
                            <!-- ชื่อคลาสกับป้ายกำกับอยู่ในคอลัมน์เดียวกัน (ชื่อบรรทัดบน ป้ายบรรทัดล่าง)
                                 ป้ายจึงเรียงชิดใต้ชื่อเสมอ ไม่หล่นไปกองใต้ป้ายวันที่ · ต้องเป็น element
                                 ห่อจริง ๆ ไม่ใช่แค่ order/flex-basis เพราะป้ายกำกับมีได้ 1-3 ใบ
                                 แล้วแต่รายการ -->
                            <div class="home-hist-main">
                                <!-- ชื่อคอร์ส "ของคาบนั้น" (backend ส่งมาต่อแถว) — เดิมใช้คอร์สหลักของเด็กทุกแถว
                                     ซึ่งว่างเปล่าสำหรับเด็กที่มาเรียนรายครั้งอย่างเดียว และผิดเมื่อคาบนั้นใช้คนละคอร์ส -->
                                <span class="strong home-hist-course">{{ item.coursename || item.course_shortname || studentSelected.coursename || studentSelected.course_shortname }}</span>
                                <div class="home-hist-tags">
                                    <span class="badge badge-neutral">{{ item.classtime }}</span>
                                    <span v-if="parseInt(item.per_session) === 1" class="badge badge-persession">{{ $t('bookingList.legendPerSession') }}</span>
                                    <span v-else-if="parseInt(item.freeflag) === 1" class="badge badge-trial">{{ $t('bookingList.legendTrial') }}</span>
                                    <span v-if="parseInt(item.blindflag) === 1" class="badge badge-private" v-hint="$t('reservation.blindNameHint')">
                                        <span class="mdi mdi-eye-off"></span> {{ $t('reservation.blindBadge') }}
                                    </span>
                                </div>
                                <div class="home-hist-detail">
                                    <span class="mdi mdi-pound-box-outline"></span>
                                    <span>{{ $t('table.courseRefer') }}</span>
                                    <b>{{ item.courserefer || '—' }}</b>
                                    <!-- ยกเลิกเอง: backend ตัดสินให้แล้ว (cancellable / cancel_block) — จอไม่คิดกติกาเอง
                                         ใบที่ผ่านไปแล้ว (past) ไม่ต้องบอกอะไร · ใบอนาคตที่ยกเลิกเองไม่ได้บอกเหตุผลสั้น ๆ -->
                                    <button v-if="parseInt(item.cancellable) === 1" class="id-btn id-btn-ghost home-cancel-btn"
                                        @click.stop="askCancelBooking(item)">
                                        <span class="mdi mdi-close-circle-outline"></span> {{ $t('home.cancelBooking') }}
                                    </button>
                                    <span v-else-if="item.cancel_block && item.cancel_block !== 'past'" class="t-cap home-cancel-note">
                                        {{ $t('home.cancelBlock.' + item.cancel_block) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="id-empty">
                    <div class="mdi mdi-calendar-blank-outline"></div>
                    <div style="margin-top:8px">{{ $t('home.noBooking') }}</div>
                </div>
            </div>

            <!-- ── Private lesson bookings of selected member ── -->
            <div class="isk-sec" style="margin:24px 0 10px">{{ $t('home.privateBooking') }}</div>
            <div class="scard" style="padding:0">
                <div v-if="!studentSelected" class="id-empty">
                    <div class="mdi mdi-gesture-tap"></div>
                    <div style="margin-top:8px">{{ $t('home.selectMember') }}</div>
                </div>
                <!-- loading: skeleton rows so switching members feels like a fresh fetch, not a flash -->
                <div v-else-if="loadingPrivateBooking">
                    <div v-for="i in 3" :key="'psk' + i" class="row home-hist-row">
                        <div class="id-skel" style="width:38px;height:38px;border-radius:10px"></div>
                        <div class="col" style="gap:4px;flex:1">
                            <div class="id-skel" style="height:13px;width:45%"></div>
                            <div class="id-skel" style="height:11px;width:30%"></div>
                        </div>
                    </div>
                </div>
                <div v-else-if="memberPrivateBookingDetail && memberPrivateBookingDetail.length > 0" class="id-fade-in" :key="'priv-' + studentid">
                    <div v-for="item in memberPrivateBookingDetail" :key="item.bookingdate + item.classtime" class="row home-hist-row">
                        <div class="home-datebadge">
                            <span class="home-datebadge-d">{{ dateBadge(item.bookingdate).d }}</span>
                            <span class="home-datebadge-m">{{ dateBadge(item.bookingdate).m }}</span>
                        </div>
                        <div style="flex:1;min-width:0">
                            <div class="strong">{{ coachLabel(item) }}</div>
                            <div class="t-cap"><span class="mdi mdi-clock-outline"></span> {{ item.classtime }}</div>
                            <div v-if="otherStudentNames(item)" class="t-cap">{{ $t('home.privateBookingWith', { names: otherStudentNames(item) }) }}</div>
                        </div>
                        <span v-if="privateHours(item) != null" class="badge badge-neutral">{{ $t('grouping.privateHours', { n: privateHours(item) }) }}</span>
                    </div>
                </div>
                <div v-else class="id-empty">
                    <div class="mdi mdi-calendar-blank-outline"></div>
                    <div style="margin-top:8px">{{ $t('home.noPrivateBooking') }}</div>
                </div>
            </div>
        </template>

        <!-- ══ ยืนยันยกเลิกการจอง (ผู้ปกครอง) ══ -->
        <id-modal v-model="cancelDlg" size="sm" icon="mdi-alert-outline" :title="$t('home.cancelTitle')">
            <div v-if="cancelTarget">
                {{ $t('home.cancelConfirm', { name: studentSelected && studentSelected.nickname, date: $formatShortDate(cancelTarget.classdate), time: cancelTarget.classtime }) }}
                <div v-if="parseInt(cancelTarget.refund_session) === 1" class="t-cap" style="margin-top:8px">
                    <span class="mdi mdi-backup-restore"></span> {{ $t('home.cancelRefund') }}
                </div>
            </div>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="cancelDlg = false">{{ $t('btn.close') }}</button>
                <button class="id-btn id-btn-primary" style="background:var(--c-error)" :disabled="cancelSaving" @click="submitCancelBooking">
                    <span class="mdi mdi-close"></span> {{ $t('home.cancelBooking') }}
                </button>
            </template>
        </id-modal>
    </div>
</template>

<script>
import { computed } from 'vue';
import axios from 'axios';
import moment from 'moment'
import { mapGetters } from 'vuex';
import { monthShortNames } from '@/utils/locale';
import EventSignup from './EventSignup.vue';
export default {
    components: { EventSignup },
    setup() {
        const isAuthenticated = computed(() => !!localStorage.getItem('token'));
        return { isAuthenticated };
    },
    methods: {
        selectChild(student) {
            this.studentid = student.studentid
            this.studentSelected = student
            this.getReservationDetail(student.studentid)
            this.getPrivateBookingDetail(student.studentid)
        },
        doReservation() {
            if (this.studentSelected == null) {
                this.$emit('onErrorHandler', this.$t('home.errorSelectFirst'))
                return
            }
            if(this.studentSelected.courserefer == null){
                this.$emit('onErrorHandler', this.$t('home.errorNoCourse'))
                return;
            }
            if (this.studentSelected.expiredate && (new Date(this.studentSelected.expiredate) < new Date())) {
                this.$emit('onErrorHandler', this.$t('home.errorExpired'))
                return;
            }
            if (this.studentSelected.coursetype != 'Monthly') {
                if (this.studentSelected.remaining <= 0) {
                    this.$emit('onErrorHandler', this.$t('home.errorNoRemaining'))
                    return;
                }
            }
            this.$emit('collectData', this.studentSelected)
            this.$emit('onClickChangeState', 'reservation')
            this.$emit('initBack', 'home')
        },
        // จองพร้อมกันหลายคน — หน้า GroupReservation โหลดรายชื่อลูกเองจาก /getFamilyMember
        doGroupReservation() {
            this.$emit('onClickChangeState', 'groupreservation')
            this.$emit('initBack', 'home')
        },
        async getFamilyMember() {
            this.$emit('onLoading', true)
            const token = this.$store.getters.getToken;
            const user = JSON.parse(localStorage.getItem('userdata'))
            await axios
                .post(this.baseURL + '/getFamilyMember', {
                    familyid: user.familyid,
                },
                    {
                        headers: { Authorization: `Bearer ${token}`, }
                    })
                .then(response => {
                    if (response.data.success) {
                        this.familylist = response.data.results
                        this.loadFamilyImages()
                    }
                })
                .catch(() => {
                    //console.error(error);
                });
            this.$emit('onLoading', false)
        },
        async loadFamilyImages() {
            if (!this.familylist || this.familylist.length === 0) return;
            const token = this.$store.getters.getToken;
            await Promise.all(
                this.familylist.map(async (member) => {
                    if (member.profile_image_url) return;
                    try {
                        const response = await axios.get(
                            this.baseURL + `/student/${member.studentid}/profile-image`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (response.data.imageUrl) {
                            member.profile_image_url = response.data.imageUrl;
                        } else if (response.data.image) {
                            member.profile_image_url = `data:image/*;base64,${response.data.image}`;
                        }
                    } catch (e) {
                        // ไม่มีรูป — ใช้ default
                    }
                })
            );
        },
        askCancelBooking(item) {
            this.cancelTarget = item
            this.cancelDlg = true
        },
        async submitCancelBooking() {
            const token = this.$store.getters.getToken;
            this.cancelSaving = true
            this.$emit('onLoading', true)
            try {
                const r = await axios.post(this.baseURL + '/cancelBookingByCustomer', {
                    reservationid: this.cancelTarget.reservationid,
                    studentid: this.studentSelected.studentid,
                }, { headers: { Authorization: `Bearer ${token}` } })
                if (r.data && r.data.success) {
                    this.cancelDlg = false
                    this.$emit('onSuccessHandler', this.$t('home.cancelDone'))
                    // ครั้งคงเหลือบนการ์ดเด็กเปลี่ยนด้วย — โหลดทั้งรายชื่อครอบครัวและรายการจองใหม่
                    this.getFamilyMember()
                    await this.getReservationDetail(this.studentSelected.studentid)
                } else {
                    // ด่านฝั่ง backend มีรหัสเหตุผล → ใช้ข้อความ 5 ภาษาของจอ · ไม่มีรหัส → ข้อความจาก backend
                    const reason = r.data && r.data.reason
                    this.$emit('onErrorHandler', reason ? this.$t('home.cancelBlock.' + reason)
                        : ((r.data && r.data.message) || this.$t('common.serverError')))
                    // รายการที่เห็นอาจเก่าแล้ว (ถูกยกเลิกจากอีกเครื่อง/โดยเจ้าหน้าที่) — ปิดกล่องแล้วโหลดใหม่เสมอ
                    this.cancelDlg = false
                    await this.getReservationDetail(this.studentSelected.studentid)
                }
            } catch (e) {
                this.$emit('onErrorHandler', this.$t('common.serverError'))
            } finally {
                this.cancelSaving = false
                this.$emit('onLoading', false)
            }
        },
        async getReservationDetail(studentid) {
            const token = this.$store.getters.getToken;
            this.loadingHistory = true
            const t0 = Date.now()
            await axios
                // courserefer ยังส่งอยู่เพื่อรองรับ backend รุ่นเดิมที่ยังกรองด้วยฟิลด์นี้
                // (เผื่อ frontend ขึ้น prod ก่อน backend) — รุ่นใหม่รับไว้แต่ไม่ใช้ คีย์ด้วย studentid เท่านั้น
                .post(this.baseURL + '/getMemberReservationDetail', {
                    studentid: studentid,
                    courserefer: this.studentSelected.courserefer
                },
                    {
                        headers: { Authorization: `Bearer ${token}`, }
                    })
                .then(response => {
                    if (response.data.success) {
                        this.memberReservationDetail = response.data.results
                    } else {
                        this.memberReservationDetail = []
                    }
                })
                .catch(() => {
                    //console.error(error);
                })
                .finally(async () => {
                    await this.$minLoad(t0)
                    this.loadingHistory = false
                });
        },
        async getPrivateBookingDetail(studentid) {
            const token = this.$store.getters.getToken;
            this.loadingPrivateBooking = true
            const t0 = Date.now()
            await axios
                .post(this.baseURL + '/getMemberPrivateBookingDetail', {
                    studentid: studentid,
                },
                    {
                        headers: { Authorization: `Bearer ${token}`, }
                    })
                .then(response => {
                    if (response.data.success) {
                        this.memberPrivateBookingDetail = response.data.results
                    } else {
                        this.memberPrivateBookingDetail = []
                    }
                })
                .catch(() => {
                    //console.error(error);
                })
                .finally(async () => {
                    await this.$minLoad(t0)
                    this.loadingPrivateBooking = false
                });
        },
        // coach display name — tolerate a couple of likely aliases from the backend
        // (full concatenated name, or a raw username fallback if names are missing)
        // ฝั่งลูกค้า: แสดง "โค้ช" + ชื่อต้นของโค้ช (เช่น โค้ชมานะ) แทนชื่อจริงเต็ม — เป็นมิตรกว่าและไม่เผยนามสกุล
        coachLabel(item) {
            const name = item.coachfirstname || item.coachusername || '';
            return name ? this.$t('home.coachName', { name }) : '';
        },
        // other student(s) sharing the same private session (2on1/3on1). The backend
        // sends already-formatted display strings (real names, or a masked placeholder
        // for other families' children) — render whatever it sends, array or string.
        otherStudentNames(item) {
            const raw = item.otherstudents ?? item.otherstudentnames;
            if (!raw || (Array.isArray(raw) && raw.length === 0)) return null;
            return Array.isArray(raw) ? raw.join(', ') : raw;
        },
        // lesson-hours for a private booking — prefer the backend's hours field,
        // else derive from the "HH:MM-HH:MM" classtime range (half-hour precision)
        privateHours(item) {
            if (item.hours != null && item.hours !== '' && !isNaN(Number(item.hours))) return Number(item.hours)
            const m = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/.exec(item.classtime || '')
            if (!m) return null
            const mins = (Number(m[3]) * 60 + Number(m[4])) - (Number(m[1]) * 60 + Number(m[2]))
            return mins > 0 ? Math.round(mins / 30) / 2 : null
        },
        format_date(value) {
            if (value) {
                return moment(value).format('DD/MM/YYYY')
            }
        },
        // วันที่/เดือนย่อสำหรับป้ายวันที่ (home-datebadge) ในลิสต์ประวัติการจอง
        // audit E13: เดือนต้องตามภาษาที่เลือก — เดิม hardcode ไทย ผู้ใช้ en/zh/ja/ko เห็นเดือนไทยตลอด
        dateBadge(value) {
            if (!value) return { d: '', m: '' }
            const m = moment(value)
            return { d: m.format('DD'), m: monthShortNames()[m.month()] }
        },
        calculateAge(birthDate) {
            if (!birthDate) return;
            const currentDate = new Date();
            if (new Date(birthDate) > currentDate) {
                alert(this.$t('common.invalidDob'))
                return;
            }
            const diffTime = currentDate - new Date(birthDate);
            const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            let years = Math.floor(totalDays / 365.25);
            let months = Math.floor((totalDays % 365.25) / 30.4375);
            let days = Math.floor((totalDays % 365.25) % 30.4375);
            return this.$t('home.ageUnit', { years, months, days })
        },
        isExpire(expdate) {
            if(!expdate) return true;
            const today = new Date();
            const expirationDate = new Date(expdate);
            return expirationDate < today;
        },
        daysUntil(d) {
            if (!d) return null;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const target = new Date(d); target.setHours(0, 0, 0, 0);
            return Math.round((target - today) / 86400000);
        },
        // `total` = original purchased quota from getFamilyMember (Limited courses;
        // NULL for Monthly, = remaining for trial owners). Fall back to a last-~8-
        // sessions gauge only on the rare chance it's missing.
        courseTotal(p) {
            const remaining = Number(p.remaining) || 0;
            const total = Number(p.total);
            return total > 0 ? total : Math.max(remaining, 8);
        },
        remainPct(p) {
            const remaining = Number(p.remaining) || 0;
            return Math.max(0, Math.min(100, (remaining / this.courseTotal(p)) * 100));
        },
        remainColor(p) {
            const r = Number(p.remaining) || 0;
            if (r <= 2) return '#ef4444';
            if (r <= 4) return '#f59e0b';
            return 'primary';
        },
        // segmented quota "pips": one block per purchased session, filled = remaining
        pipCount(p) { return Math.max(1, this.courseTotal(p)); },
        remainNum(p) { return Math.max(0, Number(p.remaining) || 0); },
        pipFill(p) { const c = this.remainColor(p); return c === 'primary' ? 'var(--c-primary)' : c; },
        monthlyFill(p) { const c = this.monthlyColor(p); return c === 'primary' ? 'var(--c-primary)' : c; },
        remainingText(p) {
            const remaining = Number(p.remaining) || 0;
            if (Number(p.total) > 0) return this.$t('home.remainOf', { n: remaining, total: Number(p.total) });
            return this.$t('home.remainBadge', { n: remaining });
        },
        // Monthly courses are unlimited but time-boxed — show progress toward expiry.
        // If the backend stores an expiry date we drain toward it over a ~30-day window;
        // otherwise the subscription is assumed valid through the end of the current
        // calendar month, so the bar always renders (consistent across every card).
        monthlyRemain(p) {
            if (p.expiredate) return { daysLeft: this.daysUntil(p.expiredate), totalDays: 30 };
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            return { daysLeft: lastDay - today.getDate(), totalDays: lastDay };
        },
        monthlyDaysText(p) {
            const { daysLeft } = this.monthlyRemain(p);
            return daysLeft > 0 ? this.$t('home.daysValue', { n: daysLeft }) : this.$t('home.expired');
        },
        monthlyPct(p) {
            const { daysLeft, totalDays } = this.monthlyRemain(p);
            return Math.max(0, Math.min(100, ((daysLeft || 0) / totalDays) * 100));
        },
        // สีของแถบรายเดือน: แดง <= 7 วัน · ส้ม <= 14 วัน · นอกนั้นสีหลัก
        // ตัวเลข 7 เคยอ่านจาก data `NEAR_DAYS` ซึ่งเป็นเกณฑ์ของแถบเตือนที่ย้ายไปกระดิ่งแล้ว —
        // เขียนตรง ๆ ตรงนี้เพราะเป็นคนละเรื่องกัน (อันนี้แค่ระดับสีบนการ์ด ไม่ใช่เกณฑ์การเตือน
        // ซึ่งอยู่ฝั่ง backend ที่เดียวแล้ว) การผูกกลับไปหาค่าเดียวกันอีกจะทำให้เกณฑ์เตือนกระจายสองที่
        monthlyColor(p) {
            const { daysLeft } = this.monthlyRemain(p);
            if (daysLeft === null || daysLeft <= 7) return '#ef4444';
            if (daysLeft <= 14) return '#f59e0b';
            return 'primary';
        },
        async uploadPhotoFile(file, studentid) {
            if (!file) return;
            if (file.size > 4 * 1024 * 1024) {
                this.$emit('onErrorHandler', this.$t('msg.fileTooLarge'));
                return;
            }
            const ext = file.name.split('.').pop();
            const renamedFile = new File([file], `${studentid}.${ext}`, { type: file.type });
            const formData = new FormData();
            formData.append('profileImage', renamedFile);
            formData.append('studentid', String(studentid));
            try {
                this.$emit('onLoading', true);
                const response = await fetch(this.baseURL + '/uploadProfileImage', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${this.token}` },
                    body: formData,
                });
                const data = await response.json();
                if (data.url) {
                    const member = this.familylist.find(p => p.studentid === studentid);
                    if (member) member.profile_image_url = data.url;
                    this.$emit('onSuccessHandler', this.$t('msg.uploadSuccess'));
                }
            } catch (e) {
                this.$emit('onErrorHandler', this.$t('msg.uploadFail'));
            } finally {
                this.$emit('onLoading', false);
            }
        },
    },
    props: {
        student: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            familylist: null,
            userdetail: null,
            memberReservationDetail: null,
            memberPrivateBookingDetail: null,
            cancelDlg: false,
            cancelTarget: null,
            cancelSaving: false,
            studentSelected: null,
            loadingHistory: false,
            loadingPrivateBooking: false,
            studentid: null,
            options: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            },
        };
    },
    computed: {
        ...mapGetters({
            token: 'getToken',
        }),
        profileGirl() {
            return require('@/assets/avatar/2.png')
        },
        profileBoy() {
            return require('@/assets/avatar/4.png')
        },
        // จำนวนลูกที่มีคอร์ส (นับโชว์ปุ่มจองหลายคน — เงื่อนไขละเอียดรายคนไปเช็คในหน้า GroupReservation)
        groupBookableCount() {
            if (!this.familylist) return 0;
            return this.familylist.filter(p => p.courserefer).length;
        },
    },
    async created() {
        this.$emit('onLoading', true)
        try {
            const token = this.$store.getters.getToken;
            if (!token) {
                this.$emit('onClickChangeState', 'login')
                return;
            }
            await axios
                .post(this.baseURL + '/verifyToken', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                .then(() => {
                    this.getFamilyMember()
                    if (this.student != null) {
                        this.selectChild(this.student)
                    }
                })
                .catch(error => {
                    this.$emit('onErrorHandler', error.response.data.message)
                    this.$emit('onClickChangeState', 'login')
                });
        } catch (error) {
            this.$emit('onErrorHandler', error.message)
        }
    },
}
</script>

<style scoped>
/* segmented quota meter — one pip per session, filled = remaining (drains as used) */
/* บรรทัด "ใช้ร่วมกับ ..." ใต้แถบโควตา — เป็นสถานะปกติของแพ็กเกจ ไม่ใช่คำเตือน
   จึงเป็นข้อความเทาเงียบ ๆ ไม่ใช่ v-alert (หน้านี้ถอด v-alert ออกหมดแล้วเมื่อ 5 ส.ค.) */
.shared-line { display: flex; align-items: flex-start; gap: 6px; font-size: 12.5px;
  line-height: 1.5; color: var(--c-text-muted); margin: -4px 0 10px; }
.shared-line .mdi { flex: 0 0 auto; font-size: 15px; line-height: 1.35; }
.quota-pips { display: flex; flex-wrap: wrap; gap: 3px; }
.quota-pip { flex: 1 1 8px; min-width: 8px; height: 8px; border-radius: 3px; background: var(--c-surface-3); transition: background .2s ease; }
/* monthly: continuous draining bar — fill anchored to the right, recedes as days pass */
.quota-bar { display: flex; justify-content: flex-end; height: 8px; border-radius: 4px; background: var(--c-surface-3); overflow: hidden; }
.quota-bar-fill { height: 100%; border-radius: 4px; transition: width .3s ease; }

/* ── Student card (selectable) ── */
.home-scard {
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
    /* flex column so the "Book a class" button can be pinned to the bottom — cards
       in a grid row are equal height (align-items:stretch), so every button lines up */
    display: flex;
    flex-direction: column;
}

/* pin the booking button to the bottom of every card regardless of content height */
.home-scard > .id-btn-primary {
    margin-top: auto;
}

.home-scard:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
}

.home-scard--active {
    border-color: var(--c-primary);
    box-shadow: 0 0 0 1px var(--c-primary), var(--shadow-md);
}

/* clip the profile image inside the round avatar chip */
.home-card-avatar {
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
    font-size: 20px;
    overflow: hidden;
    /* the header .row centres its items, so a name plus a subtitle that wraps to two lines
       makes the block taller than the avatar and the avatar drifts down beside the subtitle
       instead of the name. Anchoring to the top of the line pins it to the name. No-op while
       the avatar is still the tallest item, so short cards look exactly as before. */
    align-self: flex-start;
}

/* the status badge is the avatar's following sibling, i.e. only the one in the card header
   (the quota rows below have no avatar to follow). Same reason as above — left centred it
   floats down beside the subtitle while the avatar sits up at the name. */
.home-card-avatar ~ .badge {
    align-self: flex-start;
}

/* plain <img>, NOT <v-img>: v-img is lazy and only starts loading once Vuetify's
   v-intersect directive reports isIntersecting — inside this 52px grid/overflow:hidden
   chip that callback never arrived, so it sat in .v-img--booting forever and never
   even created an <img>. Same pattern as the working topbar avatar in App.vue. */
.home-card-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: inherit;
}

.home-hist-row {
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--c-border);
}

/* คอลัมน์ที่สองของแถว: ชื่อคลาสบรรทัดบน ป้ายกำกับบรรทัดล่าง ชิดซ้ายตรงกันทั้งคู่
   แทนของเดิมที่ป้ายเป็นพี่น้องระดับเดียวกับชื่อในแถว flex เดียว ซึ่งบนจอแคบป้าย
   (white-space:nowrap ย่อไม่ได้) บีบชื่อจนเหลือ 0 แล้วล้นทับกัน */
.home-hist-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
}

.home-hist-course {
    max-width: 100%;
    overflow-wrap: break-word;
}

.home-hist-tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.home-hist-row:last-child {
    border-bottom: none !important;
}

/* แถวการจองคลาสถูกห่อด้วย .home-hist-item เพื่อให้เส้นคั่นอยู่ที่ตัวห่อ
   → ย้ายเส้นคั่นมาไว้ที่ตัวห่อ ไม่งั้นทุกแถวจะกลายเป็น :last-child ของตัวเองแล้วเส้นหายหมด */
.home-hist-item {
    border-bottom: 1px solid var(--c-border);
}

.home-hist-item:last-child {
    border-bottom: none;
}

.home-hist-item .home-hist-row {
    border-bottom: none;
}

/* บรรทัดหมายเลขคอร์ส + ยกเลิก อยู่ในคอลัมน์ข้อความของแถว ต่อจากป้ายเวลา */
.home-hist-detail {
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: stretch;
    font-size: 12.5px;
    color: var(--c-text-muted);
}

.home-hist-detail b {
    overflow-wrap: anywhere;
    color: var(--c-text-body);
    letter-spacing: .2px;
}

/* ปุ่ม/ข้อความยกเลิกเองชิดขวาของแถวรายละเอียด · จอแคบให้ขึ้นบรรทัดใหม่แทนการล้น */
.home-hist-detail { flex-wrap: wrap; }
.home-cancel-btn { margin-left: auto; color: var(--c-error); }
.home-cancel-note { margin-left: auto; }

/* ป้ายวันที่ (แทนอวตาร์เดิมที่ซ้ำทุกแถว) — วันที่ตัวใหญ่ + เดือนย่อ
   align-self:stretch ทำให้เป็นคอลัมน์สูงเต็มแถวคู่กับคอลัมน์ชื่อคลาส+ป้ายที่สูง 2 บรรทัด
   (แถวตั้ง align-items:center ไว้ ถ้าไม่ stretch ป้ายจะหดเท่าเนื้อหาแล้วลอยกลาง) */
.home-datebadge {
    flex: 0 0 54px;
    width: 54px;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    background: var(--c-surface-2);
    border-radius: var(--radius-sm);
    padding: 6px 0;
}

.home-datebadge-d {
    display: block;
    font-family: var(--font-head);
    font-weight: 700;
    font-size: 22px;
    color: var(--c-primary);
    line-height: 1.1;
}

.home-datebadge-m {
    display: block;
    font-size: 11px;
    color: var(--c-text-muted);
    text-transform: uppercase;
    letter-spacing: .02em;
}

/* ชื่อเล่นสมาชิกที่เลือกอยู่ ต่อท้ายหัวข้อ "การจอง" — ไม่หนา/ไม่ใช่สีหัวเรื่อง ให้ต่างจากป้ายหลัก */
.home-sec-sub {
    font-family: var(--font-body);
    font-weight: 400;
    font-size: 12.5px;
    color: var(--c-text-muted);
}

/* "ส่วนตัว" (blind) badge — slate-grey ("hidden"), matches the roster/BookingManagement
   marker. NOT blue (blue = trial). */
.badge-private {
    background: rgba(100, 116, 139, 0.14);
    color: #475569;
}
.badge-private .mdi {
    font-size: 14px;
    margin-right: 2px;
}
</style>
